import { useCallback } from 'react';
import { captureWorkspaceLayoutPreset } from './captureWorkspaceLayoutPreset';
import { applyWorkspaceLayoutPreset } from './applyWorkspaceLayoutPreset';
import type { WorkspaceLayoutPreset } from 'generated/graphql';
import { useSetRecoilState } from 'recoil';
import { activeWorkspaceLayoutPresetState } from './state';
import { useToasterDispatch } from 'common/ui/Toaster';
import { logger } from 'modules/logger';

export function useWorkspaceLayoutManager() {
  const setActiveWorkspaceLayoutPreset = useSetRecoilState(activeWorkspaceLayoutPresetState);
  const { enqueueToast } = useToasterDispatch();

  const capture = useCallback(async () => {
    try {
      return await captureWorkspaceLayoutPreset();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      logger.error(
        `[WorkspaceLayoutManager] Failed to capture workspace layout preset: ${errorMessage}`,
        error
      );
      enqueueToast(`Unable to capture workspace layout preset. ${errorMessage}`, {
        severity: 'error',
      });

      return undefined;
    }
  }, [enqueueToast]);

  const apply = useCallback(
    (preset: Pick<WorkspaceLayoutPreset, 'id' | 'name' | 'windows'>) =>
      applyWorkspaceLayoutPreset(preset),
    []
  );

  const select = useCallback(
    (preset: Pick<WorkspaceLayoutPreset, 'id' | 'name' | 'windows' | 'displayArrangementKey'>) => {
      setActiveWorkspaceLayoutPreset({
        presetId: preset.id,
        displayArrangementKey: preset.displayArrangementKey,
      });
      apply(preset);
    },
    [apply, setActiveWorkspaceLayoutPreset]
  );

  return {
    captureWorkspaceLayoutPreset: capture,
    applyWorkspaceLayoutPreset: apply,
    selectWorkspaceLayoutPreset: select,
  };
}
